import React, { useEffect, useState } from "react";
import {
  PlusOutlined,
  DeploymentUnitOutlined,
  BranchesOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { Row, Col, Button, Tabs, message } from "antd";
import PageHeader from "../../components/generic/PageHeader";
import ScheduledRuns from "./ScheduledRuns";
import "../../styles/db.css";
import { useNavigate } from "react-router-dom";
import {
  getMasterSuiteLibrary,
  getMasterSuiteStepsByName,
} from "../../service/mastersuite.service";
import MasterSuiteCopyModal from "./copy/MasterSuiteCopyModal";
import {
  changeCategoryKey,
  changeSubCategoryKey,
} from "../../helper/string_helper";
import MasterSuiteDuplicateModal from "./duplicate/MasterSuiteDuplicateModal";
import { goToMasterSuiteView } from "../../helper/masterSuiteNav";
import ColumnToggle from "../../components/generic/ColumnToggle";
import TableComponent from "../../components/table/TableComponent";
import RunModal from "./run/RunModal";
import { useMasterSuiteLibraryGrid } from "./useMasterSuiteLibraryGrid";

function MasterSuite(props) {
  const embed = Boolean(props.embed);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    props.initialTab === "scheduled" ? "scheduled" : "library",
  );
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [libraryData, setLibraryData] = useState([]);
  const [viewDisplayName, setViewDisplayName] = useState("");
  const [viewDefaultEnvironment, setViewDefaultEnvironment] = useState("");
  const [existingStepData, setExistingStepData] = useState([]);
  const [copyDate, setCopyData] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(!embed);

  const getAllLibraryData = (silent = false) => {
    if (!silent) setLibraryLoading(true);
    getMasterSuiteLibrary()
      .then((response) => {
        setLibraryData(response.data);
      })
      .catch((error) => {
        message.error(`Error retrieving library data ${error}`);
      })
      .finally(() => {
        if (!silent) setLibraryLoading(false);
      });
  };

  const handleCopyClose = (visible) => {
    setCopyModalVisible(visible);
  };
  const handleDuplicateClose = (visible) => {
    setDuplicateModalVisible(visible);
  };
  const handleViewModal = (name, environment) => {
    goToMasterSuiteView(navigate, name, environment);
  };
  const handleCopyModal = (name, environment) => {
    setViewDefaultEnvironment(environment);
    setViewDisplayName(name);
    getMasterSuiteStepsByName(name).then((response) => {
      const enhancedData = response.data.map((item, index) => ({
        ...item,
        key: item.sequence,
        category: changeCategoryKey(item.categoryValue),
        subcategory: changeSubCategoryKey(item.subCategoryValue),
        count: item.sequence,
      }));
      setExistingStepData(enhancedData);
      setDuplicateModalVisible(!duplicateModalVisible);
    });
  };
  const handleEditModal = (name, environment) => {
    console.log("here ");
    setViewDefaultEnvironment(environment);
    setViewDisplayName(name);
    getMasterSuiteStepsByName(name).then((response) => {
      console.log("My data ", response.data);
      const enhancedData = response.data.map((item, index) => ({
        ...item,
        key: item.sequence,
        category: changeCategoryKey(item.categoryValue),
        subcategory: changeSubCategoryKey(item.subCategoryValue),
        count: item.sequence,
      }));
      setExistingStepData(enhancedData);
      setCopyModalVisible(true);
    });
  };

  const { mergedColumns, columnToggleProps, runModalProps } =
    useMasterSuiteLibraryGrid({
      embed,
      handleViewModal,
      handleCopyModal,
      handleEditModal,
      getAllLibraryData,
    });

  useEffect(() => {
    if (!embed) {
      getAllLibraryData();
    } else {
      setLibraryLoading(false);
    }
  }, [embed]);

  // Auto-refresh (silently) while any suite is running; stop once none are.
  const hasRunning = libraryData.some(
    (r) => r.lastRunStatus === "In Progress",
  );
  useEffect(() => {
    if (embed || !hasRunning) return;
    const id = setInterval(() => getAllLibraryData(true), 5000);
    return () => clearInterval(id);
  }, [embed, hasRunning]);

  const tableRows = embed ? (props.filterData ?? []) : libraryData;
  const effectiveLoading = embed
    ? Boolean(props.loading)
    : libraryLoading;

  const modals = (
    <Row>
      <MasterSuiteCopyModal
        displayName={viewDisplayName}
        defaultEnvironment={viewDefaultEnvironment}
        visible={copyModalVisible}
        existingData={existingStepData}
        handleModal={handleCopyClose}
        getAllLibraryData={getAllLibraryData}
        libraryData={libraryData}
      />
      <MasterSuiteDuplicateModal
        displayName={viewDisplayName}
        defaultEnvironment={viewDefaultEnvironment}
        getAllLibraryData={getAllLibraryData}
        existingData={existingStepData}
        visible={duplicateModalVisible}
        handleModal={handleDuplicateClose}
        libraryData={libraryData}
      />
    </Row>
  );

  const libraryGrid = (
    <Row
      className={
        embed ? "table-container ms-library-table-embed" : "table-container"
      }
    >
      <TableComponent
        wrapClassName="ms-library-table-host"
        tableLayout="fixed"
        style={{ width: "100%" }}
        columns={mergedColumns}
        data={tableRows}
        pageSize={20}
        loading={effectiveLoading}
        rowKey={(record) => record.name ?? record.key}
      />
    </Row>
  );

  // Embedded inside Results — keep the simple header + grid, no tabs.
  if (embed) {
    return (
      <div className="db-container ms-master-suite-page">
        {modals}
        <PageHeader
          icon={<DeploymentUnitOutlined />}
          title="Master Suite Library"
          tooltip="Manage and run master suite"
          actions={<ColumnToggle {...columnToggleProps} />}
        />
        {libraryGrid}
        <RunModal {...runModalProps} />
      </div>
    );
  }

  // Standalone page — Library and Scheduled tabs under one header.
  return (
    <div className="db-container ms-master-suite-page">
      {modals}
      <PageHeader
        icon={<DeploymentUnitOutlined />}
        title="Master Suite"
        tooltip="Manage, run and schedule master suites"
        actions={
          activeTab === "library" ? (
            <>
              <Button
                className="db-add"
                icon={<PlusOutlined />}
                onClick={() => navigate("/mastersuite/new")}
              >
                New Suite
              </Button>
              <ColumnToggle {...columnToggleProps} />
            </>
          ) : null
        }
      />
      <Tabs
        className="ms-tabs"
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "library",
            label: (
              <>
                <BranchesOutlined />
                Library
              </>
            ),
            children: libraryGrid,
          },
          {
            key: "scheduled",
            label: (
              <>
                <ClockCircleOutlined />
                Scheduled
              </>
            ),
            children: <ScheduledRuns asTab />,
          },
        ]}
      />
      {activeTab === "library" && <RunModal {...runModalProps} />}
    </div>
  );
}

export default MasterSuite;
